import {
  Box,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Typography,
  alpha,
} from '@mui/material';
import { useState } from 'react';
import { APPLICATION_STATUS_LIST } from '../../../constants/statusList';
import { APPLICATION_STATUS_COLORS } from '../../../constants/statusColors';
import { DATE_RANGE_OPTIONS } from '../../../constants/dateRangeOptions';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale/ko';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BusinessIcon from '@mui/icons-material/Business';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import { getInflowColor } from '../../../constants/inflowChannels';

type Props = {
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  onSearchChange: (text: string) => void;
  selectedDateRange: string;
  onDateRangeChange: (range: string) => void;
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  // 특수 필터
  showIncomplete?: boolean;
  onShowIncompleteChange?: (show: boolean) => void;
  showManualOnly?: boolean;
  onShowManualOnlyChange?: (show: boolean) => void;
  showIndividualOnly?: boolean;
  onShowIndividualOnlyChange?: (show: boolean) => void;
  // 📈 유입경로 필터
  selectedInflow?: string;
  onInflowChange?: (value: string) => void;
  inflowOptions?: { value: string; label: string }[];
};

/**
 * 🎨 프리미엄 관리자 필터 헤더
 * - 글래스모피즘 디자인
 * - 상태별 컬러 버튼
 * - 검색 + 기간 필터 통합
 */
const OrdersHeader = ({
  selectedStatus,
  onStatusChange,
  onSearchChange,
  selectedDateRange,
  onDateRangeChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  showIncomplete = false,
  onShowIncompleteChange,
  showManualOnly = false,
  onShowManualOnlyChange,
  showIndividualOnly = false,
  onShowIndividualOnlyChange,
  selectedInflow = '전체',
  onInflowChange,
  inflowOptions = [],
}: Props) => {
  const [searchText, setSearchText] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    onSearchChange(value);
  };

  // 🎨 프리미엄 컬러 팔레트
  const colors = {
    primary: '#1a1a2e',
    accent: '#4361ee',
    accentLight: '#7b8cff',
    glass: 'rgba(255, 255, 255, 0.85)',
    glassBorder: 'rgba(255, 255, 255, 0.3)',
    shadowLight: 'rgba(67, 97, 238, 0.08)',
    shadowMedium: 'rgba(67, 97, 238, 0.15)',
  };

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: 4,
        overflow: 'hidden',
        // 🌟 프리미엄 글래스모피즘 배경
        background: `linear-gradient(135deg, 
          ${alpha(colors.accent, 0.03)} 0%, 
          ${alpha('#fff', 0.95)} 50%, 
          ${alpha(colors.accentLight, 0.05)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(colors.accent, 0.08)}`,
        boxShadow: `
          0 4px 24px ${colors.shadowLight},
          0 1px 2px ${alpha('#000', 0.04)},
          inset 0 1px 0 ${alpha('#fff', 0.8)}
        `,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentLight}, ${colors.accent})`,
          opacity: 0.7,
        },
      }}
    >
      {/* 🏷️ 섹션 헤더 */}
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          pt: 2.5,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FilterListIcon sx={{ fontSize: 18, color: colors.accent, opacity: 0.8 }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: 13,
            color: colors.primary,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}
        >
          필터 & 검색
        </Typography>
      </Box>

      {/* 🔹 상태 필터 */}
      <Box sx={{ px: { xs: 2.5, md: 3 }, pb: 2 }}>
        {isMobile ? (
          <FormControl fullWidth size="small">
            <InputLabel id="status-select-label">상태 필터</InputLabel>
            <Select
              labelId="status-select-label"
              value={selectedStatus}
              label="상태 필터"
              onChange={(e) => onStatusChange(e.target.value)}
              sx={{
                borderRadius: 2.5,
                bgcolor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(colors.accent, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(colors.accent, 0.3),
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.accent,
                  borderWidth: 2,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 3,
                    boxShadow: `0 8px 32px ${alpha('#000', 0.12)}`,
                    mt: 1,
                  },
                },
              }}
            >
              {APPLICATION_STATUS_LIST.map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  sx={{
                    color: APPLICATION_STATUS_COLORS[status],
                    fontWeight: 600,
                    py: 1.2,
                    '&:hover': {
                      bgcolor: alpha(APPLICATION_STATUS_COLORS[status], 0.08),
                    },
                  }}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Stack
            direction="row"
            spacing={0.8}
            flexWrap="wrap"
            sx={{
              p: 0.6,
              borderRadius: 3,
              bgcolor: alpha('#f8f9fc', 0.8),
              border: `1px solid ${alpha(colors.accent, 0.06)}`,
            }}
          >
            {APPLICATION_STATUS_LIST.map((status) => {
              const isSelected = selectedStatus === status;
              const statusColor = APPLICATION_STATUS_COLORS[status];

              return (
                <Box
                  key={status}
                  onClick={() => onStatusChange(status)}
                  sx={{
                    px: 2,
                    py: 0.9,
                    borderRadius: 2.5,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                    letterSpacing: '-0.3px',
                    userSelect: 'none',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    // 선택 상태
                    bgcolor: isSelected ? statusColor : 'transparent',
                    color: isSelected ? '#fff' : statusColor,
                    boxShadow: isSelected
                      ? `0 4px 14px ${alpha(statusColor, 0.35)}, 0 2px 6px ${alpha(statusColor, 0.2)}`
                      : 'none',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                    // 호버 효과
                    '&:hover': {
                      bgcolor: isSelected ? statusColor : alpha(statusColor, 0.1),
                      transform: 'scale(1.02)',
                    },
                    '&:active': {
                      transform: 'scale(0.98)',
                    },
                    // 광택 효과
                    '&::after': isSelected
                      ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '50%',
                          background: `linear-gradient(to bottom, ${alpha('#fff', 0.15)}, transparent)`,
                          borderRadius: 'inherit',
                        }
                      : {},
                  }}
                >
                  {status}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* 🆕 특수 필터 (정보 미완성 / 수동 신청) */}
      {(onShowIncompleteChange || onShowManualOnlyChange) && (
        <Box
          sx={{
            px: { xs: 2.5, md: 3 },
            pb: 2,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {/* 정보 미완성 필터 */}
          {onShowIncompleteChange && (
            <Box
              onClick={() => onShowIncompleteChange(!showIncomplete)}
              sx={{
                px: 2,
                py: 0.8,
                borderRadius: 2.5,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                userSelect: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                bgcolor: showIncomplete ? '#f59e0b' : alpha('#f59e0b', 0.08),
                color: showIncomplete ? '#fff' : '#d97706',
                border: `1.5px solid ${showIncomplete ? '#f59e0b' : alpha('#f59e0b', 0.3)}`,
                boxShadow: showIncomplete
                  ? '0 4px 14px rgba(245, 158, 11, 0.35)'
                  : 'none',
                '&:hover': {
                  bgcolor: showIncomplete ? '#d97706' : alpha('#f59e0b', 0.15),
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 16 }} />
              정보 미완성
            </Box>
          )}

          {/* 수동/거래처 신청 필터 */}
          {onShowManualOnlyChange && (
            <Box
              onClick={() => onShowManualOnlyChange(!showManualOnly)}
              sx={{
                px: 2,
                py: 0.8,
                borderRadius: 2.5,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                userSelect: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                bgcolor: showManualOnly ? '#7c3aed' : alpha('#7c3aed', 0.08),
                color: showManualOnly ? '#fff' : '#7c3aed',
                border: `1.5px solid ${showManualOnly ? '#7c3aed' : alpha('#7c3aed', 0.3)}`,
                boxShadow: showManualOnly
                  ? '0 4px 14px rgba(124, 58, 237, 0.35)'
                  : 'none',
                '&:hover': {
                  bgcolor: showManualOnly ? '#6d28d9' : alpha('#7c3aed', 0.15),
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              <BusinessIcon sx={{ fontSize: 16 }} />
              수동 신청건
            </Box>
          )}

          {/* 일반 접수(B2C 개인고객) 필터 */}
          {onShowIndividualOnlyChange && (
            <Box
              onClick={() => onShowIndividualOnlyChange(!showIndividualOnly)}
              sx={{
                px: 2,
                py: 0.8,
                borderRadius: 2.5,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                userSelect: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                bgcolor: showIndividualOnly ? '#0ea5e9' : alpha('#0ea5e9', 0.08),
                color: showIndividualOnly ? '#fff' : '#0ea5e9',
                border: `1.5px solid ${showIndividualOnly ? '#0ea5e9' : alpha('#0ea5e9', 0.3)}`,
                boxShadow: showIndividualOnly
                  ? '0 4px 14px rgba(14, 165, 233, 0.35)'
                  : 'none',
                '&:hover': {
                  bgcolor: showIndividualOnly ? '#0284c7' : alpha('#0ea5e9', 0.15),
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 16 }} />
              일반 접수
            </Box>
          )}
        </Box>
      )}

      {/* 🔹 검색 + 기간 필터 */}
      <Box
        sx={{
          px: { xs: 2.5, md: 3 },
          py: 2,
          bgcolor: alpha('#f8fafc', 0.5),
          borderTop: `1px solid ${alpha(colors.accent, 0.05)}`,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          {/* 🔍 프리미엄 검색 입력 */}
          <TextField
            size="small"
            placeholder="신청번호, 이름, 전화번호, 모델, 매장코드 검색..."
            value={searchText}
            onChange={handleSearchChange}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      fontSize: 20,
                      color: isSearchFocused ? colors.accent : alpha(colors.primary, 0.35),
                      transition: 'all 0.2s',
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: { xs: '100%', md: 340 },
              maxWidth: { md: 480 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#fff',
                fontSize: 14,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderColor: alpha(colors.accent, 0.12),
                  borderWidth: 1.5,
                  transition: 'all 0.2s',
                },
                '&:hover fieldset': {
                  borderColor: alpha(colors.accent, 0.25),
                },
                '&.Mui-focused': {
                  bgcolor: '#fff',
                  boxShadow: `0 0 0 4px ${alpha(colors.accent, 0.1)}, 0 4px 16px ${alpha(colors.accent, 0.12)}`,
                  '& fieldset': {
                    borderColor: colors.accent,
                    borderWidth: 2,
                  },
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: alpha(colors.primary, 0.4),
                opacity: 1,
              },
            }}
          />

          {/* 📅 기간 필터 */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* 📈 유입경로 필터 */}
            {onInflowChange && inflowOptions.length > 0 && (
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
                <InputLabel
                  sx={{
                    '&.Mui-focused': { color: colors.accent },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <CampaignIcon sx={{ fontSize: 16 }} />
                    유입경로
                  </Stack>
                </InputLabel>
                <Select
                  label={
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <CampaignIcon sx={{ fontSize: 16 }} />
                      유입경로
                    </Stack>
                  }
                  value={selectedInflow}
                  onChange={(e) => onInflowChange(e.target.value)}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(colors.accent, 0.12),
                      borderWidth: 1.5,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(colors.accent, 0.25),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.accent,
                      borderWidth: 2,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: 3,
                        boxShadow: `0 8px 32px ${alpha('#000', 0.12)}`,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          py: 1.2,
                          fontSize: 14,
                          '&:hover': {
                            bgcolor: alpha(colors.accent, 0.06),
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(colors.accent, 0.1),
                            '&:hover': {
                              bgcolor: alpha(colors.accent, 0.15),
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {inflowOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        {option.value !== '전체' && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: getInflowColor(option.value),
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {option.label}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
              <InputLabel
                sx={{
                  '&.Mui-focused': { color: colors.accent },
                }}
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <CalendarMonthIcon sx={{ fontSize: 16 }} />
                  기간
                </Stack>
              </InputLabel>
              <Select
                label={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <CalendarMonthIcon sx={{ fontSize: 16 }} />
                    기간
                  </Stack>
                }
                value={selectedDateRange}
                onChange={(e) => onDateRangeChange(e.target.value)}
                sx={{
                  borderRadius: 3,
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(colors.accent, 0.12),
                    borderWidth: 1.5,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(colors.accent, 0.25),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.accent,
                    borderWidth: 2,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 3,
                      boxShadow: `0 8px 32px ${alpha('#000', 0.12)}`,
                      mt: 1,
                      '& .MuiMenuItem-root': {
                        py: 1.2,
                        fontSize: 14,
                        '&:hover': {
                          bgcolor: alpha(colors.accent, 0.06),
                        },
                        '&.Mui-selected': {
                          bgcolor: alpha(colors.accent, 0.1),
                          '&:hover': {
                            bgcolor: alpha(colors.accent, 0.15),
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {DATE_RANGE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 📆 사용자 지정 날짜 선택 */}
            {selectedDateRange === '사용자 지정' && (
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{
                    animation: 'slideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    '@keyframes slideIn': {
                      from: { opacity: 0, transform: 'translateX(-10px)' },
                      to: { opacity: 1, transform: 'translateX(0)' },
                    },
                  }}
                >
                  <DatePicker
                    label="시작일"
                    value={startDate}
                    onChange={onStartDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 150,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#fff',
                            '& fieldset': {
                              borderColor: alpha(colors.accent, 0.12),
                              borderWidth: 1.5,
                            },
                            '&:hover fieldset': {
                              borderColor: alpha(colors.accent, 0.25),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.accent,
                              borderWidth: 2,
                            },
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="종료일"
                    value={endDate}
                    onChange={onEndDateChange}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          minWidth: 150,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: '#fff',
                            '& fieldset': {
                              borderColor: alpha(colors.accent, 0.12),
                              borderWidth: 1.5,
                            },
                            '&:hover fieldset': {
                              borderColor: alpha(colors.accent, 0.25),
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: colors.accent,
                              borderWidth: 2,
                            },
                          },
                        },
                      },
                    }}
                  />
                </Stack>
              </LocalizationProvider>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default OrdersHeader;
